import React, { createContext, useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';

export const AuthContext = createContext();
const API_BASE = '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [authConfig, setAuthConfig] = useState({
    googleEnabled: false,
    otpTestMode: false,
    emailOtpEnabled: false,
    whatsappOtpEnabled: false,
    paymentProvider: 'razorpay',
    paymentConfigured: false,
    paymentCurrency: 'INR',
    razorpayConfigured: false,
    razorpayCurrency: 'INR',
  });

  const refreshAuthConfig = useCallback(async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/config`);
        if (!res.ok) {
          return;
        }
        const data = await res.json();
        const normalizedCurrency = String(data.paymentCurrency || data.razorpayCurrency || 'INR')
          .trim()
          .toUpperCase();
        const razorpayConfigured = Boolean(data.razorpayConfigured);
        const paymentConfigured = Boolean(data.paymentConfigured ?? razorpayConfigured);
        const paymentCurrency = /^[A-Z]{3}$/.test(normalizedCurrency) ? normalizedCurrency : 'INR';
        const normalizedRazorpayCurrency = String(data.razorpayCurrency || 'INR')
          .trim()
          .toUpperCase();
        setAuthConfig({
          googleEnabled: Boolean(data.googleEnabled),
          otpTestMode: Boolean(data.otpTestMode),
          emailOtpEnabled: Boolean(data.emailOtpEnabled),
          whatsappOtpEnabled: Boolean(data.whatsappOtpEnabled),
          paymentProvider: String(data.paymentProvider || 'razorpay'),
          paymentConfigured,
          paymentCurrency,
          razorpayConfigured,
          razorpayCurrency: /^[A-Z]{3}$/.test(normalizedRazorpayCurrency)
            ? normalizedRazorpayCurrency
            : 'INR',
        });
      } catch (error) {
        console.error('Failed to fetch auth config', error);
      }
  }, []);

  useEffect(() => {
    refreshAuthConfig();
  }, [refreshAuthConfig]);

  const clearSession = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setUsers([]);
  }, []);

  const fetchUsers = useCallback(async () => {
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE}/users`, {
        headers: { 'x-auth-token': token },
      });

      if (!res.ok) {
        if (res.status === 400 || res.status === 401 || res.status === 403) {
          clearSession();
          return;
        }
        throw new Error('Failed to fetch users');
      }

      const data = await res.json();
      setUsers(data);
    } catch (error) {
      console.error('Failed to fetch users', error);
      setUsers([]);
    }
  }, [token, clearSession]);

  useEffect(() => {
    let isMounted = true;

    const validateSession = async () => {
      setLoading(true);

      if (!token) {
        if (isMounted) {
          setUser(null);
          setLoading(false);
        }
        return;
      }

      try {
        const decoded = jwtDecode(token);
        const expiresAtMs = Number(decoded?.exp || 0) * 1000;
        if (!expiresAtMs || Date.now() >= expiresAtMs) {
          if (isMounted) {
            clearSession();
            setLoading(false);
          }
          return;
        }

        const res = await fetch(`${API_BASE}/auth/me`, {
          headers: { 'x-auth-token': token },
        });

        if (!res.ok) {
          throw new Error('Session is invalid');
        }

        const data = await res.json();
        if (isMounted) {
          setUser(data);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Failed to validate token', error);
          clearSession();
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    validateSession();

    return () => {
      isMounted = false;
    };
  }, [token, clearSession]);

  useEffect(() => {
    if (user && (user.role === 'admin' || user.role === 'superadmin')) {
      fetchUsers();
    } else {
      setUsers([]);
    }
  }, [user, fetchUsers]);

  const logout = () => {
    clearSession();
  };

  const completeOAuthLogin = (nextToken) => {
    localStorage.setItem('token', nextToken);
    setToken(nextToken);
    setLoading(true);
  };

  const toggleUserRole = async (userId) => {
    if (!token) {
      throw new Error('You must be logged in.');
    }

    try {
      const res = await fetch(`${API_BASE}/users/${userId}/toggle-role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        if (res.status === 400 || res.status === 401 || res.status === 403) {
          clearSession();
          throw new Error('Session expired. Please sign in again.');
        }
        throw new Error(data.msg || 'Failed to toggle user role');
      }

      fetchUsers();
    } catch (error) {
      console.error('Failed to toggle user role', error);
      throw error;
    }
  };

  const acceptTermsOfService = async () => {
    if (!token) {
      throw new Error('You must be logged in.');
    }

    const res = await fetch(`${API_BASE}/auth/accept-tos`, {
      method: 'POST',
      headers: {
        'x-auth-token': token,
      },
    });

    const data = await res.json();

    if (!res.ok) {
      if (res.status === 400 || res.status === 401 || res.status === 403) {
        clearSession();
        throw new Error('Session expired. Please sign in again.');
      }
      throw new Error(data.msg || 'Failed to accept Terms of Service.');
    }

    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        users,
        authConfig,
        refreshAuthConfig,
        loading,
        logout,
        completeOAuthLogin,
        toggleUserRole,
        acceptTermsOfService,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
